import React from 'react';
import styled from 'styled-components';
import { Container as _Container, Typo, Buttons } from 'tmt-ui-kit';
// import { scroller, Element } from 'react-scroll';

import Element from '~/components/Scroll/Element';
import RehydrateComponent from '~/components/RehydrateComponent';
import HeadTag from '~/components/Head/HeadTag';
import Collapse from '~/components/Collapse/Collapse';
import Register from '~/components/Affiliate/Register';
import Detail from '~/components/Affiliate/Detail';
import Testimonials from '~/components/Affiliate/Testimonials';

import affiliateData from '~/utils/affiliate/data';
import useWindowSize from '~/utils/hooks/use-window-size';
import scrollTo from '~/utils/scroller';

const Container = styled(_Container.InnerContainer)`
  z-index: 1;
  width: 100%;
  padding-bottom: 80px;
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: 2%;

  @media(max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media(max-width: 600px) {
    ${props => props.content && 'padding: 0px;'}
    padding-bottom: 50px;
  }
`;

const Banner = styled.div`
  position: relative;
  height: 400px;
  display: flex;
  flex-direction: column;
  background: url('/static/images/Affiliate/banner.jpg');
  background-size: cover;
  background-position: center;

  @media(max-width: 600px) {
    display: flex;
    justify-content: flex-end;
    background: url('/static/images/Affiliate/mobile-banner.jpg');
    background-size: cover;
    background-position: center;
    height: 570px;
  }
`;

const BannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(80deg, white 40%, rgba(0, 0, 0, .0) 55%) no-repeat;
  background-size: cover;
  background-position: left;

  @media(max-width: 768px) {
    background: linear-gradient(80deg, white 55%, rgba(0, 0, 0, .0) 67%) no-repeat;
  }
  @media(max-width: 600px) {
    background: linear-gradient(to top, white 30%, rgba(0, 0, 0, .0) 67%) no-repeat;
    background-size: cover;
    background-position: left;
  }
`;

const BannerContent = styled.div`
  position: relative;  
  z-index: 1;
  padding-top: 64px;
`;

const Title = styled(Typo.HeaderTwo)`
  font-size: 28px !important;
  color: ${props => props.theme.darkBlue} !important;
  font-weight: 600 !important;

  @media(max-width: 600px) {
    margin: 0;
  }
`;

const Subtitle = styled(Typo.Paragraph)`
  font-size: 18px !important;
  font-weight: 600 !important;
  color: ${props => props.theme.darkBlue} !important;
`;

const Intro = styled.div`
  margin-top: 28px;
  padding-left: 24px;
  border-left: 5px solid ${props => props.theme.orange};

  @media(max-width: 600px) {
    padding:0px 16px;
  }
`;

const Text = styled(Typo.Paragraph)`
  font-size: 16px !important;
`;

const Bold = styled.span`
  font-weight: ${props => (props.weight ? props.weight : '500')};
  ${props => props.title && `
    color: ${props.theme.orange};
    font-size: 20px;
  `}
`;

const Col = styled.div`
  width: 100%;
`;

const TitleContent = styled(Title)`
  font-size: 20px !important;
  @media(max-width: 600px) {
    margin: 28px 0;
  }
`;

const FAQContainer = styled.div`
  margin: 56px 0px;
`;

const CollapseTitle = styled(Typo.Paragraph)`
  font-size: 16px !important;
  margin: 0;
  ${props => props.bold && 'font-weight: 500 !important;'}
`;

const ContactUs = styled.div`
  @media(max-width: 600px) {
    padding:0px 16px;
  }
`;

const TitleContactUs = styled(Typo.HeaderTwo)`
  margin: 4px 0;
`;

const RegisterButton = styled(Buttons.Button.OrangeButton)`
  width: 148px;
  margin: 32px 0px 0px;
  box-shadow: 0 3px 15px 0 ${props => props.theme.orange};
  cursor: pointer;

  @media(max-width: 600px) {
    margin-top: 14px;
  }
`;

const ContactButton = styled(Buttons.Button.TextButton)`
  width: 148px;
  margin: 24px 0px 0px;
  background: transparent;
  color: ${props => props.theme.orange};
  font-weight: 500;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
  border: solid 1px ${props => props.theme.orange};
`;

const TextContent = styled.div`
  @media(max-width: 600px) {
    padding:0px 16px;
  }
`;

const Desktop = styled.div`
  @media(max-width: 600px) {
    display: none;
  }
`;

const Mobile = styled.div`
  display: none;
  @media(max-width: 600px) {
    display: block;
  }
`;

const TextFAQ = styled.span`
  @media(max-width: 600px) {
    margin: 28px 16px;
  }
`;

const Affiliate = () => {
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const scrollName = isMobile ? 'register-mebile' : 'register-desktop';

  return (
    <div>
      <HeadTag
        title="Affiliate - TakeMeTour"
        image="/static/images/Affiliate/affiliate_preview.jpg"
      />
      <Banner>
        <Container>
          <BannerContent>
            <Title>Be an Affiliate</Title>
            <Subtitle>Travel and Increase Your Earnings with <br/>TakeMeTour Affiliate Program</Subtitle>
            <RegisterButton
              onClick={() => {
                scrollTo(scrollName, {
                  offset: -140,
                  duration: 500,
                  smooth: true,
                });
              }}
            >
              Register
            </RegisterButton>
          </BannerContent>
        </Container>
        <BannerOverlay />
      </Banner>
      <Container content>
        <Col>
          <Intro>
            <Text><Bold title>TakeMeTour is the largest platform for selective private local tours in Thailand.</Bold> We have also expanded operations into Cambodia, Vietnam and the Philippines in 2019. With unique local tours and many essential travel products, you can easily earn and refer your audience to amazing deals by becoming our affiliate!</Text>
          </Intro>
          <Detail />
          <RehydrateComponent>
            <TextContent>
              <TitleContent>How much will you earn?</TitleContent>
              <Text>
                With 1000+ local tours spanning Thailand, Cambodia, Vietnam, the Philippines and Indonesia, you will have numerous opportunities to earn a commission <Bold weight="500">starting from 6%</Bold> with every booking included wide range of essential travel products such as airport transfers, sim cards, attraction tickets and blind massage.
              </Text>
              <TitleContent>How does the program work?</TitleContent>
              <Text>
                Simply use our affiliate tools to monitor and manage your site traffic and revenue. We also have <Bold weight="500">30-day Cookies</Bold> that ensure visitors who made a booking or purchase through your site will show up on your <Bold weight="500">affiliate dashboard</Bold>.
              </Text>
              <Testimonials />
            </TextContent>
          </RehydrateComponent>
          <Mobile>
            <Element name="register-mebile" />
            <RehydrateComponent>
              <Register isMobile={isMobile} />
            </RehydrateComponent>
          </Mobile>
          <RehydrateComponent>
            <FAQContainer>
              <TitleContent>
                <TextFAQ>FAQ</TextFAQ>
              </TitleContent>
              {affiliateData.map((data, index) => (
                <Collapse
                  key={data.title}
                  isChangeBg={index % 2 === 0}
                  renderTitle={() => <CollapseTitle>{data.title}</CollapseTitle>}
                  renderBody={() => <Text>{data.content}</Text>}
                />
              ))}
            </FAQContainer>
            <ContactUs>
              <TitleContactUs>Can’t find your answer?</TitleContactUs>
              <Text>
                Get in touch with us and we’ll get back to you as soon as possible.
              </Text>
              <a href="mailto:affiliate@takemetour.com"><ContactButton>Contact Us</ContactButton></a>
            </ContactUs>
          </RehydrateComponent>
        </Col>
        <Col>
          <Desktop>
            <Element name="register-desktop" />
            <RehydrateComponent>
              <Register isMobile={isMobile} />
            </RehydrateComponent>
          </Desktop>
        </Col>
      </Container>
    </div>
  );
};

export default Affiliate;
